import { ErrorReportingUtils } from '@mm/plugins';
import { useGuidesCRUDActions } from '@mm/state/actions/GuidesCRUDActions';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { LocalStorageService } from '@mm/utils';
import { get } from 'lodash';
import * as queryString from 'query-string';
import { useEffect, useLayoutEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { useSelector } from 'react-redux';
import { initNPSPlugin } from './common/helpers/plugins/nps-plugin-init';
import { PROMO_CODE_KEY } from './dashboard/pages/RedeemPromoCode/RedeemPromoCode';
import { GlobalHooksContainerProps } from './GlobalHooksContainer';
import { Routes } from './routes/top-level-routes';

export const useInitialModal = (props: GlobalHooksContainerProps) => {
  const { location, openInitialModal } = props;
  useEffect(() => {
    const search = queryString.parse(location.search);
    if (search.initModal) {
      openInitialModal(search.initModal as string);
    }
  }, []);
};

export const useAddPromoToUser = (props: GlobalHooksContainerProps) => {
  const { session, companyId, addPromoToUser } = props;

  const applyPromo = async () => {
    if (session && companyId) {
      const promoCode = LocalStorageService.getItem(PROMO_CODE_KEY);

      if (promoCode) {
        try {
          await addPromoToUser(companyId, promoCode);
          LocalStorageService.removeItem(PROMO_CODE_KEY);
        } catch (error) {
          ErrorReportingUtils.reportError('Redeem Promo Code Error', {
            extraData: { error },
          });
        }
      }
    }
  };

  useEffect(() => {
    applyPromo();
  }, [session, companyId]);
};

export const useSetUserContextForPlugins = (
  props: GlobalHooksContainerProps,
) => {
  const { session } = props;

  useEffect(() => {
    if (session) {
      ErrorReportingUtils.setUserContext(session.id, session.email);
    }
  }, [session]);
};

export const useInitNPSPlugin = (props: GlobalHooksContainerProps) => {
  const { session } = props;
  const [needsInit, setNeedsInit] = useState(true);

  useEffect(() => {
    if (needsInit && session) {
      setNeedsInit(false);
      initNPSPlugin(session);
    }
  }, [session, needsInit, setNeedsInit]);
};

export const useFetchCompaniesOnSession = (
  props: GlobalHooksContainerProps,
) => {
  const { session, getCompanies, location } = props;
  const isLogoutRoute = get(location, 'pathname') === Routes.signout.path;

  const userId = session ? session.id : undefined;

  useEffect(() => {
    if (userId && !isLogoutRoute) {
      getCompanies();
    }
  }, [userId]);
};

export const useGoogleAnalyticsTrackPages = (
  props: GlobalHooksContainerProps,
) => {
  const { location, gaTrackingId } = props;

  useEffect(() => {
    if (gaTrackingId) {
      ReactGA.set({ page: location.pathname });
    }
  }, [location.pathname, gaTrackingId]);
};

export const useScrollTopRouteChange = (props: GlobalHooksContainerProps) => {
  const { location } = props;

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
};

export const useGetPlans = (props: GlobalHooksContainerProps) => {
  const { getPlans, session, companyId } = props;
  const sessionId = session ? session.id : undefined;

  useEffect(() => {
    if (sessionId && companyId) {
      getPlans(companyId);
    }
  }, [sessionId, getPlans, companyId]);
};

export const useVersionWatcher = (props: GlobalHooksContainerProps) => {
  const { notifyMandatoryUpdate, notifyRecommendedUpdate } = props;
  const [secondsToReload, setSecondsToReload] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    if (secondsToReload) {
      notifyMandatoryUpdate(secondsToReload);
      const timeoutId = setTimeout(() => {
        if (secondsToReload === 1) {
          window.location.reload();
        }
        setSecondsToReload(secondsToReload - 1);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?v=${Date.now()}`);
        const latestVersion = (await response.json()) as typeof BUILD_VERSION;
        if (BUILD_VERSION.major !== latestVersion.major) {
          // Drop everything and reload.
          setSecondsToReload(30);
          notifyMandatoryUpdate(30);
        } else if (BUILD_VERSION.minor !== latestVersion.minor) {
          // Show warning.
          notifyRecommendedUpdate();
        }
      } catch {
        // Ignore errors.
      }
    };
    const intervalId = setInterval(checkVersion, 30000);
    return () => clearInterval(intervalId);
  }, [secondsToReload, notifyMandatoryUpdate, notifyRecommendedUpdate]);
};

export const useFetchGuides = () => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const guidesCRUDActions = useGuidesCRUDActions();

  useEffect(() => {
    if (chatbotId > 0) {
      guidesCRUDActions.retrieveAll();
    }
  }, [chatbotId, guidesCRUDActions]);
};
